import React, { useRef, useLayoutEffect, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PageWrapper from './PageWrapper';
import EditableText from './EditableText';
import { selectPageSlice } from '../Selector';
import { reportPageCount } from '../../../redux/actions';

const PAGE_HEIGHT = 1123;
const PADDING_TOP = 50;
const PADDING_BOTTOM = 60;
const FOOTER_HEIGHT = 40;
const CONTENT_HEIGHT = PAGE_HEIGHT - PADDING_TOP - PADDING_BOTTOM - FOOTER_HEIGHT; // 973px
const CONTENT_WIDTH = 794 - 60 - 60; // 674px

function ResponseTableHead() {
  return (
    <thead>
      <tr>
        <th>GROUP</th>
        <th>COMPLETED RESPONSES<br /><span className="rr-th-sub">(% Complete)</span></th>
        <th>DESCRIPTION</th>
      </tr>
    </thead>
  );
}

function isTotalRow(row) {
  return String(row[0]).trim().toLowerCase() === 'total';
}

function GroupCell({ row, rowIndex }) {
  // The Total row's group label is fixed and never editable.
  if (isTotalRow(row)) return row[0];
  return (
    <EditableText
      as="div"
      path={`firstPage.tableContents.contents[${rowIndex}][0]`}
      variant="inline"
      stripInlineWrap
    />
  );
}

function DescriptionCell({ row, rowIndex }) {
  // Row data lookup uses the global row index so the path is stable.
  // Always render the editor — even when the description is empty — so a new
  // description can be added to rows that don't have one yet. The Total row is
  // the sole exception (it never carries a description).
  if (isTotalRow(row)) return null;
  return (
    <EditableText
      as="div"
      path={`firstPage.tableContents.contents[${rowIndex}][2]`}
      variant="block"
      stripInlineWrap
    />
  );
}

function ResponseRatePage({ data, personName, slice, startPageNumber = 2, reportPageCount }) {
  const { tableContents, nominatedRaters } = data;
  const rows = tableContents.contents;

  const postTableItems = [];
  postTableItems.push(
    <div key="note">
      <EditableText
        as="p"
        className="pdf-note"
        path="firstPage.othersNote"
        variant="block"
        stripInlineWrap
      />
    </div>
  );
  postTableItems.push(
    <div key="nominated-heading">
      <EditableText
        as="h2"
        className="pdf-section-heading pdf-section-heading--sub"
        path="firstPage.nominatedHeading"
        variant="inline"
        stripInlineWrap
      />
    </div>
  );
  Object.entries(nominatedRaters || {}).forEach(([group, members]) => {
    postTableItems.push(
      <div key={`nom-${group}`} className="pdf-nominated-group">
        <h4>{group}</h4>
        <p>{Array.isArray(members) ? members.join(', ') : members}</p>
      </div>
    );
  });

  const measureRef = useRef(null);
  const [layout, setLayout] = useState(null);

  // Re-measure when the editable slice changes (after a commit). Setting
  // layout back to null forces a render of the hidden measurement tree.
  // Skip the initial mount — first measurement runs via useLayoutEffect below.
  const isFirstSliceRef = useRef(true);
  useEffect(() => {
    if (isFirstSliceRef.current) {
      isFirstSliceRef.current = false;
      return;
    }
    setLayout(null);
  }, [slice]);

  useLayoutEffect(() => {
    if (layout) return; // only measure when in phase 1
    if (!measureRef.current) return;
    const container = measureRef.current;

    const headerEl = container.querySelector('[data-rr="header"]');
    const theadEl = container.querySelector('[data-rr="thead"]');
    const tableEl = container.querySelector('[data-rr="table"]');
    const trEls = Array.from(container.querySelectorAll('tr[data-rr-row]'));
    const postEls = Array.from(container.querySelectorAll('[data-rr-post]'));

    const containerTop = container.getBoundingClientRect().top;

    const headerH = headerEl ? headerEl.getBoundingClientRect().height : 0;
    const theadH = theadEl ? theadEl.getBoundingClientRect().height : 0;
    const actualTableH = tableEl ? tableEl.getBoundingClientRect().height : 0;
    const rowHeights = trEls.map(tr => tr.getBoundingClientRect().height);

    const sumOfRowH = rowHeights.reduce((s, h) => s + h, 0);
    const tableOverhead = Math.max(0, actualTableH - theadH - sumOfRowH);

    const tableTopY = tableEl
      ? tableEl.getBoundingClientRect().top - containerTop
      : headerH;

    const page1Avail = CONTENT_HEIGHT - tableTopY - theadH - tableOverhead;
    const tablePageAvail = CONTENT_HEIGHT - theadH - tableOverhead;

    const tablePages = [];
    let pageRowIdxs = [];
    let usedH = 0;
    let isFirstPage = true;

    rows.forEach((_, idx) => {
      const h = rowHeights[idx] || 0;
      const avail = isFirstPage ? page1Avail : tablePageAvail;
      if (pageRowIdxs.length > 0 && usedH + h > avail) {
        tablePages.push(pageRowIdxs);
        pageRowIdxs = [idx];
        usedH = h;
        isFirstPage = false;
      } else {
        pageRowIdxs.push(idx);
        usedH += h;
      }
    });
    if (pageRowIdxs.length > 0) tablePages.push(pageRowIdxs);

    const tableBottomY = tableEl
      ? tableEl.getBoundingClientRect().bottom - containerTop
      : headerH;

    const gapAfterTable = (postEls.length > 0 && tableEl)
      ? postEls[0].getBoundingClientRect().top - tableEl.getBoundingClientRect().bottom
      : 0;

    let spaceForPost;
    if (tablePages.length <= 1) {
      spaceForPost = CONTENT_HEIGHT - tableBottomY - Math.max(0, gapAfterTable);
    } else {
      const lastPageRowsH = tablePages[tablePages.length - 1].reduce(
        (sum, rIdx) => sum + (rowHeights[rIdx] || 0), 0
      );
      spaceForPost = CONTENT_HEIGHT - theadH - tableOverhead - lastPageRowsH - Math.max(0, gapAfterTable);
    }

    const postBaseY = postEls.length > 0 ? postEls[0].getBoundingClientRect().top : 0;
    const onTablePage = [];

    for (let idx = 0; idx < postEls.length; idx++) {
      const cumH = postEls[idx].getBoundingClientRect().bottom - postBaseY;
      if (cumH <= spaceForPost) {
        onTablePage.push(idx);
      } else {
        break;
      }
    }

    const overflowStart = onTablePage.length;
    const extraPages = [];

    if (overflowStart < postEls.length) {
      let batchStartIdx = overflowStart;

      for (let idx = overflowStart; idx < postEls.length; idx++) {
        const batchTop = postEls[batchStartIdx].getBoundingClientRect().top;
        const batchBottom = postEls[idx].getBoundingClientRect().bottom;

        if (idx > batchStartIdx && batchBottom - batchTop > CONTENT_HEIGHT) {
          const batch = [];
          for (let j = batchStartIdx; j < idx; j++) batch.push(j);
          extraPages.push(batch);
          batchStartIdx = idx;
        }
      }

      const lastBatch = [];
      for (let j = batchStartIdx; j < postEls.length; j++) lastBatch.push(j);
      if (lastBatch.length > 0) extraPages.push(lastBatch);
    }

    setLayout({ tablePages, onTablePage, extraPages });

    if (reportPageCount) {
      reportPageCount('firstPage', tablePages.length + extraPages.length);
    }
  }, [layout, rows, reportPageCount]);

  const header = (
    <EditableText
      as="h2"
      className="pdf-section-heading"
      path="firstPage.responseRateHeading"
      variant="inline"
      stripInlineWrap
    />
  );

  // ── Phase 1: measurement ──────────────────────────────────────────────────
  if (!layout) {
    return (
      <div style={{ position: 'relative', height: 0, overflow: 'hidden' }}>
        <div
          ref={measureRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: CONTENT_WIDTH,
            visibility: 'hidden',
            pointerEvents: 'none',
          }}
        >
          <div data-rr="header">{header}</div>
          <table data-rr="table" className="pdf-table pdf-table--response-rate">
            <thead data-rr="thead">
              <tr>
                <th>GROUP</th>
                <th>COMPLETED RESPONSES<br /><span className="rr-th-sub">(% Complete)</span></th>
                <th>DESCRIPTION</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx} data-rr-row={idx} className={isTotalRow(row) ? 'rr-row--total' : ''}>
                  <td><GroupCell row={row} rowIndex={idx} /></td>
                  <td>{row[1]}</td>
                  <td><DescriptionCell row={row} rowIndex={idx} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          {postTableItems.map((item, idx) => (
            <div key={idx} data-rr-post={idx}>{item}</div>
          ))}
        </div>
      </div>
    );
  }

  // ── Phase 2: render pages ─────────────────────────────────────────────────
  const { tablePages, onTablePage, extraPages } = layout;
  const totalTablePages = tablePages.length;

  const rendered = [];

  tablePages.forEach((rowIdxs, pageIdx) => {
    const isFirst = pageIdx === 0;
    const isLastTablePage = pageIdx === totalTablePages - 1;
    const pageNum = startPageNumber + pageIdx;

    rendered.push(
      <PageWrapper key={`tp-${pageIdx}`} pageNumber={pageNum} personName={personName}>
        {isFirst && header}
        <table className="pdf-table pdf-table--response-rate">
          <ResponseTableHead />
          <tbody>
            {rowIdxs.map(rIdx => (
              <tr key={rIdx} className={isTotalRow(rows[rIdx]) ? 'rr-row--total' : ''}>
                <td><GroupCell row={rows[rIdx]} rowIndex={rIdx} /></td>
                <td>{rows[rIdx][1]}</td>
                <td><DescriptionCell row={rows[rIdx]} rowIndex={rIdx} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {isLastTablePage && onTablePage.map(pIdx => postTableItems[pIdx])}
      </PageWrapper>
    );
  });

  extraPages.forEach((postIdxs, extraIdx) => {
    const pageNum = startPageNumber + totalTablePages + extraIdx;
    rendered.push(
      <PageWrapper key={`pp-${extraIdx}`} pageNumber={pageNum} personName={personName}>
        {postIdxs.map(pIdx => postTableItems[pIdx])}
      </PageWrapper>
    );
  });

  return <>{rendered}</>;
}

const mapStateToProps = (state) => ({
  slice: selectPageSlice(state, 'firstPage'),
});

const mapDispatchToProps = (dispatch) => ({
  reportPageCount: (sectionKey, count) => dispatch(reportPageCount(sectionKey, count)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ResponseRatePage);
